import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { BookOpen, ArrowLeft, Search, Heart, Library as LibraryIcon, Filter, SlidersHorizontal, X, Crown } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = 'https://yourpersonalshopper-storage.sfo3.digitaloceanspaces.com/omniread/omniread_logo.webp';

const CATEGORIES = [
  'Relationships', 'Work/Purpose', 'Identity', 'Healing',
  'Transformation/Shadow', 'Self-Worth', 'Grief/Letting Go',
  'Creativity', 'Leadership', 'Masculine/Feminine Dynamics', 'Courage'
];

const ALL_LENSES = [
  'Jungian Psychology', 'Buddhism', 'Taoism', 'Christianity',
  'Hinduism', 'Sufism/Mysticism', 'Greek Mythology', 'Norse Mythology',
  'Fairy Tale Archetypes', 'Fantasy Literature', 'Stoicism', 'Biology/Science'
];

export default function CollectiveLibrary() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [devotionals, setDevotionals] = useState([]);
  const [filteredDevotionals, setFilteredDevotionals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedDevotional, setSelectedDevotional] = useState(null);
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);

  // Filter states
  const [sortBy, setSortBy] = useState('newest'); // 'newest' or 'oldest'
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedLenses, setSelectedLenses] = useState([]);

  useEffect(() => {
    loadSubscriptionStatus();
    loadCollective();
  }, []);

  useEffect(() => {
    applyFiltersAndSort();
  }, [devotionals, sortBy, selectedCategory, selectedLenses]);

  const loadSubscriptionStatus = async () => {
    try {
      const resp = await axios.get(`${API}/subscription/status`);
      setSubscriptionStatus(resp.data);
    } catch (err) {
      console.error('Failed to load subscription status:', err);
    }
  };

  const loadCollective = async () => {
    try {
      const resp = await axios.get(`${API}/collective-library?limit=100`);
      setDevotionals(resp.data);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load collective library');
    } finally {
      setLoading(false);
    }
  };

  const applyFiltersAndSort = () => {
    let filtered = [...devotionals];

    // Filter by category
    if (selectedCategory) {
      filtered = filtered.filter(d => d.category === selectedCategory);
    }

    // Filter by lenses (show devotionals that have at least one of the selected lenses)
    if (selectedLenses.length > 0) {
      filtered = filtered.filter(d =>
        d.lenses_used.some(lens => selectedLenses.includes(lens))
      );
    }

    // Sort by date
    filtered.sort((a, b) => {
      const dateA = new Date(a.offered_at || a.created_at);
      const dateB = new Date(b.offered_at || b.created_at);
      return sortBy === 'newest' ? dateB - dateA : dateA - dateB;
    });

    setFilteredDevotionals(filtered);
  };

  const handleSearch = async () => {
    if (!searchQuery.trim() || searchQuery.trim().length < 2) {
      toast.error('Please enter at least 2 characters');
      return;
    }

    setSearching(true);
    try {
      const resp = await axios.get(`${API}/collective-library/search?q=${encodeURIComponent(searchQuery.trim())}`);
      setDevotionals(resp.data);
      if (resp.data.length === 0) {
        toast.info('No devotionals found for this search');
      }
    } catch (err) {
      toast.error('Search failed');
      console.error(err);
    } finally {
      setSearching(false);
    }
  };

  const handleReadDevotional = (devotional) => {
    if (subscriptionStatus?.tier === 'pro') {
      setSelectedDevotional(devotional);
    } else {
      setShowUpgradePrompt(true);
    }
  };

  const handleUpgrade = async (plan) => {
    try {
      const resp = await axios.post(`${API}/subscription/create-checkout`, { plan });
      window.location.href = resp.data.checkout_url;
    } catch (err) {
      toast.error('Failed to start checkout');
      console.error(err);
    }
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    loadCollective();
  };

  const toggleLensFilter = (lens) => {
    if (selectedLenses.includes(lens)) {
      setSelectedLenses(selectedLenses.filter(l => l !== lens));
    } else {
      setSelectedLenses([...selectedLenses, lens]);
    }
  };

  const clearAllFilters = () => {
    setSelectedCategory('');
    setSelectedLenses([]);
    setSortBy('newest');
  };

  const getTruncatedPreview = (content, maxWords = 25) => {
    const words = content.split(' ');
    if (words.length <= maxWords) return content;
    return words.slice(0, maxWords).join(' ') + '...';
  };

  const handleSaveFromCollective = async (devotionalId) => {
    try {
      const resp = await axios.post(`${API}/devotionals/${devotionalId}/save-from-collective`);
      if (resp.data.already_saved) {
        toast.info('Already in your library');
      } else {
        toast.success('✨ Saved to your library');
      }
    } catch (err) {
      if (err.response?.status === 403) {
        setShowUpgradePrompt(true);
      } else {
        toast.error('Failed to save');
      }
      console.error(err);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="page-container" style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh'
      }}>
        <div className="cosmic-loader"></div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="fade-in" style={{ maxWidth: '1100px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '2rem',
          flexWrap: 'wrap',
          gap: '1rem'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <Button
              onClick={() => navigate('/dashboard')}
              data-testid="back-button"
              variant="ghost"
              style={{ padding: '0.5rem' }}
            >
              <ArrowLeft size={24} color="#d4af37" />
            </Button>
            <img src={LOGO_URL} alt="OmniRead" style={{ width: '50px', height: '50px' }} />
            <div>
              <h1 style={{ fontSize: '2rem', margin: 0 }}>Collective Library</h1>
              <p style={{ color: '#d4af37', fontSize: '0.9rem', margin: 0, fontStyle: 'italic' }}>
                A peaceful archive of shared wisdom
              </p>
            </div>
          </div>

          <Button
            onClick={() => navigate('/library')}
            data-testid="my-library-button"
            className="btn-outline-gold"
          >
            <LibraryIcon size={20} style={{ marginRight: '0.5rem' }} />
            My Library
          </Button>
        </div>

        {/* Search Bar */}
        <div className="mystical-card" style={{ marginBottom: '1rem' }}>
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', marginBottom: '1rem' }}>
            <Search size={24} color="#d4af37" />
            <Input
              data-testid="search-input"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              placeholder="Search keywords... (patience, rebirth, letting go, etc.)"
              style={{
                flex: 1,
                background: 'rgba(0, 0, 0, 0.4)',
                border: '1px solid rgba(212, 175, 55, 0.3)',
                color: '#e8e8e8',
                fontSize: '1.1rem',
                padding: '0.75rem'
              }}
            />
            <Button
              onClick={handleSearch}
              data-testid="search-button"
              className="btn-gold"
              disabled={searching}
            >
              {searching ? 'Searching...' : 'Search'}
            </Button>
            {searchQuery && (
              <Button
                onClick={handleClearSearch}
                data-testid="clear-search-button"
                className="btn-outline-gold"
              >
                Clear
              </Button>
            )}
          </div>

          {/* Filter Toggle */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '1rem', borderTop: '1px solid rgba(212, 175, 55, 0.2)' }}>
            <Button
              onClick={() => setShowFilters(!showFilters)}
              className="btn-outline-gold"
              style={{ fontSize: '0.9rem' }}
            >
              <SlidersHorizontal size={18} style={{ marginRight: '0.5rem' }} />
              {showFilters ? 'Hide Filters' : 'Show Filters'}
            </Button>

            {(selectedCategory || selectedLenses.length > 0 || sortBy !== 'newest') && (
              <Button
                onClick={clearAllFilters}
                className="btn-outline-gold"
                style={{ fontSize: '0.9rem' }}
              >
                <X size={18} style={{ marginRight: '0.5rem' }} />
                Clear Filters
              </Button>
            )}
          </div>
        </div>

        {/* Filter Panel */}
        {showFilters && (
          <div className="mystical-card" style={{ marginBottom: '2rem' }}>
            {/* Sort */}
            <div style={{ marginBottom: '1.5rem' }}>
              <h3 style={{ fontSize: '1rem', marginBottom: '0.75rem', color: '#f4d03f' }}>Sort By</h3>
              <div style={{ display: 'flex', gap: '1rem' }}>
                <Button
                  onClick={() => setSortBy('newest')}
                  className={sortBy === 'newest' ? 'btn-gold' : 'btn-outline-gold'}
                  style={{ flex: 1 }}
                >
                  Latest Offerings
                </Button>
                <Button
                  onClick={() => setSortBy('oldest')}
                  className={sortBy === 'oldest' ? 'btn-gold' : 'btn-outline-gold'}
                  style={{ flex: 1 }}
                >
                  Oldest First
                </Button>
              </div>
            </div>

            {/* Category Filter */}
            <div style={{ marginBottom: '1.5rem' }}>
              <h3 style={{ fontSize: '1rem', marginBottom: '0.75rem', color: '#f4d03f' }}>Filter by Category</h3>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                {CATEGORIES.map(cat => (
                  <button
                    key={cat}
                    onClick={() => setSelectedCategory(selectedCategory === cat ? '' : cat)}
                    style={{
                      padding: '0.5rem 1rem',
                      background: selectedCategory === cat ? 'rgba(212, 175, 55, 0.2)' : 'rgba(0, 0, 0, 0.3)',
                      border: `1px solid ${selectedCategory === cat ? '#d4af37' : 'rgba(212, 175, 55, 0.3)'}`,
                      borderRadius: '20px',
                      color: selectedCategory === cat ? '#d4af37' : '#c9c9c9',
                      fontSize: '0.85rem',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            </div>

            {/* Lens Filter */}
            <div>
              <h3 style={{ fontSize: '1rem', marginBottom: '0.75rem', color: '#f4d03f' }}>Filter by Wisdom Lenses</h3>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                {ALL_LENSES.map(lens => (
                  <button
                    key={lens}
                    onClick={() => toggleLensFilter(lens)}
                    style={{
                      padding: '0.5rem 1rem',
                      background: selectedLenses.includes(lens) ? 'rgba(212, 175, 55, 0.2)' : 'rgba(0, 0, 0, 0.3)',
                      border: `1px solid ${selectedLenses.includes(lens) ? '#d4af37' : 'rgba(212, 175, 55, 0.3)'}`,
                      borderRadius: '20px',
                      color: selectedLenses.includes(lens) ? '#d4af37' : '#c9c9c9',
                      fontSize: '0.85rem',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    {lens}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Devotionals Grid */}
        {devotionals.length === 0 ? (
          <div className="mystical-card" style={{ textAlign: 'center', padding: '4rem 2rem' }}>
            <BookOpen size={64} color="#666" style={{ margin: '0 auto 1.5rem' }} />
            <h2 style={{ marginBottom: '1rem', color: '#999' }}>No offerings yet</h2>
            <p style={{ marginBottom: '2rem', color: '#666' }}>
              The collective library is waiting for its first devotional
            </p>
            <Button
              onClick={() => navigate('/dashboard')}
              className="btn-gold whitespace-normal md:whitespace-nowrap"
            >
              Generate and Offer Your First
            </Button>
          </div>
        ) : filteredDevotionals.length === 0 ? (
          <div className="mystical-card" style={{ textAlign: 'center', padding: '3rem 2rem' }}>
            <Filter size={48} color="#666" style={{ margin: '0 auto 1rem' }} />
            <h2 style={{ marginBottom: '1rem', color: '#999' }}>No devotionals match your filters</h2>
            <p style={{ marginBottom: '1.5rem', color: '#666' }}>
              Try adjusting your filters or clearing them to see all offerings
            </p>
            <Button
              onClick={clearAllFilters}
              className="btn-outline-gold"
            >
              Clear All Filters
            </Button>
          </div>
        ) : (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <p style={{ color: '#d4af37', fontSize: '1rem', margin: 0 }}>
                {filteredDevotionals.length} offering{filteredDevotionals.length !== 1 ? 's' : ''}
                {filteredDevotionals.length !== devotionals.length && ` (filtered from ${devotionals.length} total)`}
              </p>
              <p style={{ color: '#999', fontSize: '0.9rem', margin: 0 }}>
                Sorted by: {sortBy === 'newest' ? 'Latest Offerings' : 'Oldest First'}
              </p>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: '1.5rem' }}>
              {filteredDevotionals.map((devotional, idx) => (
                <div
                  key={devotional.id}
                  data-testid={`collective-devotional-${idx}`}
                  className="mystical-card fade-in"
                  style={{
                    animationDelay: `${idx * 0.05}s`,
                    padding: '1.5rem',
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%'
                  }}
                >
                  {/* Header */}
                  <div style={{ marginBottom: '1rem', paddingBottom: '1rem', borderBottom: '1px solid rgba(212, 175, 55, 0.2)' }}>
                    <h3 style={{
                      fontSize: '1.3rem',
                      fontStyle: 'italic',
                      marginBottom: '0.5rem',
                      color: '#f4d03f'
                    }}>
                      {getTruncatedPreview(devotional.topic, 10)}
                    </h3>

                    {devotional.category && (
                      <p style={{
                        fontSize: '0.8rem',
                        color: '#d4af37',
                        margin: '0 0 0.5rem 0',
                        padding: '0.25rem 0.75rem',
                        background: 'rgba(212, 175, 55, 0.1)',
                        borderRadius: '12px',
                        display: 'inline-block'
                      }}>
                        {devotional.category}
                      </p>
                    )}

                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.5rem' }}>
                      <div>
                        {devotional.offered_by_name && !devotional.offered_anonymously && (
                          <p style={{ fontSize: '0.8rem', color: '#999', margin: 0 }}>
                            By {devotional.offered_by_name}
                          </p>
                        )}
                        {devotional.offered_anonymously && (
                          <p style={{ fontSize: '0.8rem', color: '#999', margin: 0, fontStyle: 'italic' }}>
                            Anonymous
                          </p>
                        )}
                      </div>
                      <p style={{ fontSize: '0.75rem', color: '#666', margin: 0 }}>
                        {formatDate(devotional.offered_at || devotional.created_at)}
                      </p>
                    </div>
                  </div>

                  {/* Lenses */}
                  <div style={{ marginBottom: '1rem' }}>
                    <p style={{ fontSize: '0.75rem', color: '#999', marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Wisdom Lenses
                    </p>
                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                      {devotional.lenses_used.map((lens, i) => (
                        <span key={i} className="badge" style={{ fontSize: '0.7rem', padding: '0.3rem 0.75rem', background: 'rgba(212, 175, 55, 0.15)' }}>
                          {lens}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Content Preview */}
                  <div style={{
                    flex: 1,
                    fontSize: '0.9rem',
                    lineHeight: '1.6',
                    color: '#b9b9b9',
                    marginBottom: '1rem',
                    fontStyle: 'italic'
                  }}>
                    {getTruncatedPreview(devotional.content, 30)}
                  </div>

                  {/* Action Buttons */}
                  <div style={{ display: 'flex', gap: '0.75rem' }}>
                    <Button
                      onClick={() => handleReadDevotional(devotional)}
                      data-testid={`read-devotional-${idx}`}
                      className="btn-gold"
                      style={{ flex: 1 }}
                    >
                      <BookOpen size={18} style={{ marginRight: '0.5rem' }} />
                      Read
                    </Button>
                    <Button
                      onClick={() => handleSaveFromCollective(devotional.id)}
                      data-testid={`save-from-collective-${idx}`}
                      className="btn-outline-gold"
                      style={{ minWidth: '48px', padding: '0.5rem' }}
                      title="Save to My Library"
                    >
                      <Heart size={18} />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Devotional Reading Modal */}
      {selectedDevotional && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.9)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '2rem',
            overflowY: 'auto'
          }}
          onClick={() => setSelectedDevotional(null)}
        >
          <div
            className="mystical-card"
            style={{
              maxWidth: '800px',
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto',
              background: 'linear-gradient(135deg, rgba(20, 20, 30, 0.98), rgba(30, 30, 40, 0.98))',
              border: '2px solid #d4af37'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem' }}>
              <div style={{ flex: 1 }}>
                <h1 style={{ fontSize: '2rem', marginBottom: '0.5rem', color: '#f4d03f' }}>
                  {selectedDevotional.topic}
                </h1>
                {selectedDevotional.category && (
                  <p style={{
                    fontSize: '0.9rem',
                    color: '#d4af37',
                    margin: '0 0 0.75rem 0',
                    padding: '0.35rem 1rem',
                    background: 'rgba(212, 175, 55, 0.15)',
                    borderRadius: '16px',
                    display: 'inline-block'
                  }}>
                    {selectedDevotional.category}
                  </p>
                )}
                <div style={{ marginTop: '0.75rem' }}>
                  {selectedDevotional.offered_by_name && !selectedDevotional.offered_anonymously && (
                    <p style={{ fontSize: '0.9rem', color: '#999', margin: 0 }}>
                      Offered by {selectedDevotional.offered_by_name}
                    </p>
                  )}
                  {selectedDevotional.offered_anonymously && (
                    <p style={{ fontSize: '0.9rem', color: '#999', margin: 0, fontStyle: 'italic' }}>
                      Anonymous offering
                    </p>
                  )}
                </div>
              </div>
              <Button
                onClick={() => setSelectedDevotional(null)}
                variant="ghost"
                style={{ padding: '0.5rem' }}
              >
                <X size={24} color="#d4af37" />
              </Button>
            </div>

            {/* Lenses */}
            <div style={{ marginBottom: '1.5rem' }}>
              <p style={{ fontSize: '0.85rem', color: '#999', marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Through the lenses of:
              </p>
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                {selectedDevotional.lenses_used.map((lens, i) => (
                  <span key={i} className="badge" style={{ fontSize: '0.8rem', padding: '0.4rem 1rem', background: 'rgba(212, 175, 55, 0.2)' }}>
                    {lens}
                  </span>
                ))}
              </div>
            </div>

            {/* Content */}
            <div style={{
              fontSize: '1.1rem',
              lineHeight: '1.8',
              color: '#e8e8e8',
              marginBottom: '2rem',
              whiteSpace: 'pre-wrap'
            }}>
              {selectedDevotional.content}
            </div>

            {/* Action Buttons */}
            <div style={{
              display: 'flex',
              gap: '1rem',
              paddingTop: '1.5rem',
              borderTop: '1px solid rgba(212, 175, 55, 0.2)'
            }}>
              <Button
                onClick={() => {
                  handleSaveFromCollective(selectedDevotional.id);
                  setSelectedDevotional(null);
                }}
                className="btn-gold"
                style={{ flex: 1 }}
              >
                <Heart size={18} style={{ marginRight: '0.5rem' }} />
                Save to My Library
              </Button>
              <Button
                onClick={() => setSelectedDevotional(null)}
                className="btn-outline-gold"
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Upgrade Prompt Modal */}
      {showUpgradePrompt && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.85)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '1rem'
          }}
          onClick={() => setShowUpgradePrompt(false)}
        >
          <div
            className="mystical-card"
            style={{
              maxWidth: '500px',
              width: '100%',
              background: 'linear-gradient(135deg, rgba(20, 20, 30, 0.98), rgba(30, 30, 40, 0.98))',
              border: '2px solid #d4af37'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
              <Crown size={48} color="#d4af37" style={{ marginBottom: '1rem' }} />
              <h2 style={{ fontSize: '1.8rem', marginBottom: '0.5rem' }}>Pro Access Required</h2>
              <p style={{ color: '#999', margin: 0 }}>
                Unlock full access to collective readings
              </p>
            </div>

            <div style={{ marginBottom: '1.5rem', padding: '1rem', background: 'rgba(212, 175, 55, 0.05)', borderRadius: '8px' }}>
              <h3 style={{ fontSize: '1rem', marginBottom: '0.75rem' }}>With Pro you get:</h3>
              <ul style={{ margin: 0, paddingLeft: '1.5rem', color: '#c9c9c9' }}>
                <li style={{ marginBottom: '0.5rem' }}>📖 Read all devotionals in the Collective Library</li>
                <li style={{ marginBottom: '0.5rem' }}>💾 Save favorites to your personal library</li>
                <li style={{ marginBottom: '0.5rem' }}>✨ Unlimited devotional generations</li>
                <li>🔮 Access to all 6 wisdom lenses</li>
              </ul>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <Button
                onClick={() => handleUpgrade('yearly')}
                className="btn-gold"
                style={{ width: '100%', padding: '1rem', fontSize: '1.1rem' }}
              >
                $39/year <span style={{ fontSize: '0.9rem', marginLeft: '0.5rem' }}>(Save 35%)</span>
              </Button>
              <Button
                onClick={() => handleUpgrade('monthly')}
                className="btn-outline-gold"
                style={{ width: '100%', padding: '1rem', fontSize: '1.1rem' }}
              >
                $4.99/month
              </Button>
              <Button
                onClick={() => {
                  setShowUpgradePrompt(false);
                  navigate('/dashboard');
                }}
                className="btn-outline-gold"
                style={{ width: '100%', padding: '0.75rem' }}
              >
                Back to Dashboard
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}